import { Component } from '@theme/component';
import { fetchDiscountRequirementText } from '@theme/discount-info-api';

/** Shopify's `/discount/<CODE>` route stores the code in this session cookie before redirecting. */
const DISCOUNT_COOKIE = 'discount_code';

/**
 * @param {string} name
 * @returns {string} The decoded cookie value, or '' when the cookie is absent.
 */
function readCookie(name) {
  const entry = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));
  if (!entry) return '';

  const raw = entry.slice(name.length + 1);
  try {
    return decodeURIComponent(raw).trim();
  } catch {
    // A malformed escape sequence — the raw string is still a usable code to compare and show.
    return raw.trim();
  }
}

/**
 * Opens the "you got a discount" dialog (see `snippets/discount-welcome-popup.liquid`) for a shopper
 * who arrived through a `/discount/<CODE>` link, e.g. a QR code.
 *
 * Shopify redirects that URL to `/` and the code is gone from the address bar; the `discount_code`
 * cookie the route leaves behind is the only signal, and it is set for every code — valid or not. The
 * theme cannot tell them apart from an empty cart alone (both report `applicable: false`), which is
 * why `#checkStatus` also asks the automatic discount-info lookup below — a dead, scheduled, or
 * expired code resolves to the honest `ineligible` state instead of a false "✓ Otomatis terpasang".
 *
 * Shows every time a shopper lands on `/` carrying that cookie — including a repeat visit to the exact
 * same `/discount/<CODE>` link in one tab (asked for directly: "seharusnya tetap muncul... setiap kali
 * halaman discount ini di tuju"). There is deliberately no once-per-session memory anymore — an earlier
 * version had one, and it was indistinguishable from a bug during testing ("apakah saya sudah
 * terdeteksi sudah memakainya?": no, nothing tracks usage, it just wasn't re-showing).
 *
 * The `location.pathname === '/'` check below is what keeps this from becoming a nuisance rather than
 * a convenience: the `discount_code` cookie has no expiry of its own and survives every later page the
 * shopper visits for the rest of that browser session (verified: `set-cookie: discount_code=...; path=/;
 * SameSite=Lax`, no Max-Age). Without the pathname gate, the "you got a discount" dialog would pop up
 * again on the next product page, the cart, everywhere — not just on a genuine repeat visit to the
 * discount link. Since Shopify's own route always lands on `/` (confirmed against this store), gating
 * on that path is what makes "every visit to the link" and "not every page on the site" both true at
 * once. If a discount link is ever built with `?redirect=` pointing somewhere other than `/`, this
 * would need to check that destination instead.
 *
 * Wraps `dialog-component` instead of holding a `ref` to it — refs don't cross a nested `-component`
 * boundary (see `size-guide-hint.js`, `member-welcome-popup.js`), so it is reached with `querySelector`.
 *
 * @extends {Component}
 */
export class DiscountWelcomePopup extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#showIfArrivedWithCode();
  }

  async #showIfArrivedWithCode() {
    // See the class doc above for why this only ever fires on the storefront root: the cookie itself
    // has no expiry and would otherwise still be readable on every later page this session.
    if (location.pathname !== '/') return;

    const code = readCookie(DISCOUNT_COOKIE).toUpperCase();
    if (!code) return;

    const codeLabel = this.querySelector('[data-discount-popup-code]');
    // textContent, never innerHTML: the value comes straight from a cookie.
    if (codeLabel) codeLabel.textContent = code;

    // dialog.js is a separate module loaded from the head; don't assume it has registered yet.
    await customElements.whenDefined('dialog-component');

    /** @type {import('@theme/dialog').DialogComponent | null} */
    const dialog = this.querySelector('dialog-component');
    dialog?.showDialog();

    // Doesn't block showDialog(): the dialog opens immediately in its honest default ('pending')
    // state, and this only ever upgrades it once a real answer comes back.
    this.#checkStatus(code);
  }

  /**
   * Confirms — rather than assumes — whether `code` is actually doing anything in the shopper's cart
   * right now, and swaps the visible status row to match. Reported by a merchant whose voucher required
   * a minimum spend the popup never mentioned: the code sat inert in an empty cart while the dialog's
   * static markup still claimed "✓ Otomatis terpasang" (automatically applied) regardless.
   *
   * There is no way to ask Shopify *why* a code isn't applying (minimum spend, product eligibility,
   * one-per-customer, expired — the condition itself is never exposed to the theme), so this only ever
   * reports the one thing the cart JSON actually tells us: applicable or not. See the file doc for what
   * each of the three markup states means and when this picks each one.
   *
   * @param {string} code - Already uppercased by the caller.
   */
  async #checkStatus(code) {
    /** @type {{ hidden: boolean, dataset: { discountPopupStatus?: string } }[]} */
    const rows = Array.from(this.querySelectorAll('[data-discount-popup-status]'));
    const hint = this.querySelector('[data-discount-popup-hint]');

    /** @param {'pending' | 'active' | 'ineligible'} state */
    const setState = (state) => {
      for (const row of rows) row.hidden = row.dataset.discountPopupStatus !== state;
      if (hint) hint.hidden = state !== 'ineligible';
    };

    // Started in parallel with the cart check below, not after it: this asks Shopify what the code's
    // OWN condition is (via the membership system's discount-info API — see
    // assets/discount-info-api.js), which is independent of any particular cart's contents. A
    // SCHEDULED/EXPIRED code is inapplicable to every cart, empty or not, so this can give a real
    // answer even before the cart check below ever would (an empty cart alone can never confirm
    // anything — see the comment further down).
    const automaticNotePromise = fetchDiscountRequirementText(code).catch(() => null);

    /** @type {boolean | undefined} True/false once a non-empty cart's own record of this code says
     *  so; undefined when the cart genuinely can't tell (empty, or the code isn't in its list). */
    let cartConfirmedApplicable;
    try {
      const response = await fetch(`${Theme.routes.cart_url}.json`, { headers: { Accept: 'application/json' } });
      if (response.ok) {
        const cart = await response.json();
        // An empty cart reports every code `applicable: false`, valid or not (verified against this
        // store's own cart) — nothing trustworthy to read from it either way.
        if (cart.item_count) {
          /** @type {Array<{ code: string, applicable: boolean }>} */
          const discountCodes = cart.discount_codes ?? [];
          const entry = discountCodes.find((discount) => discount.code?.toUpperCase() === code);
          if (entry) cartConfirmedApplicable = entry.applicable;
        }
      }
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        console.warn('[discount-welcome-popup] Could not confirm voucher status:', error);
      }
    }

    if (cartConfirmedApplicable === true) {
      setState('active');
      return;
    }

    const automaticNote = await automaticNotePromise;

    // 'ineligible' fires on EITHER signal: a non-empty cart that confirmed the code isn't discounting
    // anything right now, OR discount-info finding a cart-independent reason it never would (scheduled/
    // expired) — that second case is real and common on this store: HELLO10/JOC-OCT were both
    // SCHEDULED to start the day after this shipped, which an empty (or even a full) cart's own
    // `applicable` boolean gives zero indication of on its own.
    if (cartConfirmedApplicable === false || automaticNote) {
      if (automaticNote && hint) {
        hint.textContent = automaticNote;
      }
      setState('ineligible');
      return;
    }

    // Neither signal had anything — stay 'pending', the honest default already in the markup.
  }
}

if (!customElements.get('discount-welcome-popup')) {
  customElements.define('discount-welcome-popup', DiscountWelcomePopup);
}
